import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';

import Modal from '@decisiv/ui-components/lib/components/Modal';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Alert from '@decisiv/ui-components/lib/components/Alert';
import { ButtonProps } from '@decisiv/ui-components/lib/components/Button';
import { useNotifications } from '@decisiv/ui-components';
import { t } from '@lingui/macro';

import { createForm, FormHandler } from 'components/Form';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import useDebouncedValue from 'hooks/useDebouncedValue';
import { API_SEARCH_DEBOUNCE_DELAY_MS } from 'pages/Management/constants';
import parseBackendErrorMessage from 'utils/parseBackendErrorMessage';

import DepotForm from './DepotForm';
import PrimaryContactForm from './PrimaryContactForm';
import buildPrimaryContactInput from './buildPrimaryContactInput';
import {
  useCreateDepotMutation,
  useManagedPlatformAccountsQuery,
  useCreateDepotAccountContactsQuery,
} from './hooks';
import useCreateDepotCapability from './useCreateDepotCapability';
import { PRIMARY_CONTACT_EMAIL_ERROR_FIELD } from './constants';

import { ContentColumn } from '../styles';

import baseValidationSchema from './validations';

type FormData = yup.InferType<typeof baseValidationSchema>;

const { Form, FormProvider } = createForm<FormData>();

const initialValues: FormData = {
  accountId: '',
  dmsId: '',
  note: '',
  name: '',
  email: '',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  primaryContactOption: 'new',
  primaryContactFirstName: '',
  primaryContactLastName: '',
  primaryContactEmail: '',
  primaryContactId: '',
};

interface ContentProps {
  visible?: boolean;
  onClose: () => void;
}

const Content = ({ visible, onClose }: ContentProps) => {
  const {
    formState: { isValid },
    getValues,
    reset,
    setError,
    clearErrors,
    watch,
  } = useFormContext();
  const { notify } = useNotifications();
  const { accountId } = useParams();

  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    description: string;
    intent: 'danger' | 'warning';
  } | null>(null);

  const [, setAccountSearch, debouncedAccountSearch] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const accountsQuery = useManagedPlatformAccountsQuery({
    variables: { page: 1, text: debouncedAccountSearch || undefined },
    skip: !!accountId,
  });

  const [, setContactSearch, debouncedContactSearch] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const primaryContactOption = watch('primaryContactOption');
  const primaryContactEmail = watch('primaryContactEmail');

  // On the global Depots page there is no `:account_id` route param; the user
  // picks the account via the DepotForm Account combobox (form field
  // `accountId`). Mirror handleSubmit's fallback so contacts load in both
  // contexts.
  const selectedAccountId = watch('accountId');
  const effectiveAccountId = accountId || selectedAccountId;

  const { data: contactsData, loading: loadingContacts } =
    useCreateDepotAccountContactsQuery(
      {
        accountId:
          effectiveAccountId ?? /* istanbul ignore next -- @preserve */ '',
        text:
          debouncedContactSearch.length < 3
            ? undefined
            : debouncedContactSearch,
      },
      { skip: !effectiveAccountId || primaryContactOption !== 'existing' },
    );

  const contacts =
    contactsData?.managedPlatformAccount?.globalContacts?.collection ?? [];

  // Clear the duplicate-contact warning + stale validation errors on the
  // (now hidden) branch fields when the radio option changes, so a hidden
  // field's error can't block `isValid`. Deliberately do NOT clear the
  // inactive branch's values: `buildPrimaryContactInput` only reads the
  // active branch and the yup schema only requires the active branch, so a
  // lingering inactive-branch value is never submitted and never blocks
  // validation. Preserving it lets the user toggle back to a branch (e.g.
  // "Select Existing Contact") and find their prior selection/typed data
  // intact instead of lost. Do reset the contact search so switching back to
  // "Select Existing Contact" shows the full contact list rather than
  // results scoped to whatever was last searched.
  useEffect(() => {
    setErrorMessage((prev) => (prev?.intent === 'warning' ? null : prev));
    clearErrors([
      'primaryContactFirstName',
      'primaryContactLastName',
      'primaryContactEmail',
      'primaryContactId',
    ]);
    setContactSearch('');
  }, [primaryContactOption, clearErrors, setContactSearch]);

  // Clear the duplicate-email warning (banner + inline) once the user edits
  // the email. Keyed on `primaryContactEmail` only so setting the warning
  // doesn't immediately clear itself; the current banner is read via a ref to
  // keep the effect body's updates as plain sequential statements (no side
  // effect inside a state updater).
  const errorMessageRef = useRef(errorMessage);
  errorMessageRef.current = errorMessage;

  useEffect(() => {
    if (errorMessageRef.current?.intent !== 'warning') return;
    setErrorMessage(null);
    clearErrors('primaryContactEmail');
  }, [primaryContactEmail, clearErrors]);

  const { capability: createDepotCapability } = useCreateDepotCapability({
    platformAccountId: accountId,
  });
  const isDenied = createDepotCapability?.allowed === false;

  const formRef = useRef<FormHandler<FormData>>(null);

  /* istanbul ignore next -- @preserve */
  const handleOnSubmit = useCallback(async () => {
    formRef.current?.submit();
  }, []);

  const clearFormAndClose = useCallback(() => {
    setErrorMessage(null);
    reset();
    onClose();
  }, [onClose, reset]);

  const showError = useCallback(
    (description: string) =>
      setErrorMessage({
        title: t`Failed to create depot.`,
        description,
        intent: 'danger',
      }),
    [],
  );

  const [createDepot, { loading }] = useCreateDepotMutation({
    onCompleted: (data) => {
      const result = data?.createAccountDepot;

      /* istanbul ignore next -- @preserve */
      if (result?.depot) {
        notify({
          title: t`Depot created successfully.`,
          intent: 'success',
          duration: 5000,
        } as any);
        clearFormAndClose();
        return;
      }

      const uniquenessError = result?.errors?.find(
        (error) => error.field === 'dms_id' && error.code === 'taken',
      );

      if (uniquenessError) {
        setError('dmsId', {
          type: 'manual',
          message: t`This FMS ID is already in use by another depot in this account.`,
        });
        return;
      }

      const emailTakenError = result?.errors?.find(
        (error) =>
          error.field === PRIMARY_CONTACT_EMAIL_ERROR_FIELD &&
          error.code === 'taken',
      );

      if (emailTakenError) {
        setErrorMessage({
          title: t`Contact Already Exists.`,
          description: t`This email is already being used by another contact, choose the Select Existing Contact option to add an existing one.`,
          intent: 'warning',
        });
        setError('primaryContactEmail', {
          type: 'manual',
          message: t`This email is already being used by another contact. Use the Select Existing Contact option.`,
        });
        return;
      }

      const [firstError] = result?.errors ?? [];
      showError(
        firstError?.message ??
          t`An unexpected error occurred. Please try again.`,
      );
    },
    onError: (error) =>
      showError(
        parseBackendErrorMessage(error) ||
          t`An unexpected error occurred. Please try again.`,
      ),
  });

  const handleSubmit = useCallback(async () => {
    setErrorMessage(null);
    const formData = getValues();
    await createDepot({
      variables: {
        platformAccountId:
          accountId ||
          /* istanbul ignore next -- @preserve */ formData.accountId,
        attributes: {
          dmsId: formData.dmsId,
          note: formData.note,
          name: formData.name,
          email: formData.email,
          address1: formData.address,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country,
          primaryContact: buildPrimaryContactInput({
            primaryContactOption: formData.primaryContactOption as
              | 'new'
              | 'existing',
            primaryContactFirstName:
              formData.primaryContactFirstName ??
              /* istanbul ignore next -- @preserve */ '',
            primaryContactLastName:
              formData.primaryContactLastName ??
              /* istanbul ignore next -- @preserve */ '',
            primaryContactEmail:
              formData.primaryContactEmail ??
              /* istanbul ignore next -- @preserve */ '',
            primaryContactId:
              formData.primaryContactId ??
              /* istanbul ignore next -- @preserve */ '',
          }),
        },
      },
    });
  }, [getValues, createDepot, accountId]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        disabled: !isValid || loading || isDenied,
        text: t`Create Depot`,
        loading,
        loadingText: t`Creating...`,
        onClick: handleSubmit,
      },
      { text: t`Cancel`, onClick: clearFormAndClose, disabled: loading },
    ];
  }, [isValid, loading, isDenied, handleSubmit, clearFormAndClose]);

  return (
    <Modal
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore //TODO: [KDS-TYPE-FIX] needs to fix the type of actions and export it
      actions={actions}
      icon={PlusCircle}
      intent="information"
      title={t`Create Depot`}
      visible={visible}
    >
      <ContentColumn>
        {errorMessage && (
          <Alert
            intent={errorMessage.intent}
            title={errorMessage.title}
            dismissable={false}
          >
            {errorMessage.description}
          </Alert>
        )}
        <Form flex={1} ref={formRef} onSubmit={handleOnSubmit}>
          <Flex flexDirection="column">
            <DepotForm
              accountId={accountId}
              accountsQuery={accountsQuery}
              capability={createDepotCapability}
              onAccountSearchChange={setAccountSearch}
            />
            <PrimaryContactForm
              contacts={contacts}
              loadingContacts={loadingContacts}
              onContactSearchChange={setContactSearch}
              disabled={isDenied}
            />
          </Flex>
        </Form>
      </ContentColumn>
    </Modal>
  );
};

interface CreateDepotModalProps {
  visible?: boolean;
  handleClose: () => void;
}

export default function CreateDepotModal({
  visible,
  handleClose,
}: CreateDepotModalProps) {
  const { accountId } = useParams();

  const validationSchema = useMemo(() => {
    return baseValidationSchema.shape({
      accountId: accountId
        ? yup.string().optional()
        : /* istanbul ignore next -- @preserve */ yup
            .string()
            .required(t`Account ID is required`),
    });
  }, [accountId]);

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <Content onClose={handleClose} visible={visible} />
    </FormProvider>
  );
}
